/* «Que tu coach te proponga el guion» (21-oct-2026). Rais: «mi idea es aportar la Biblia y que me genere el guion».
 *
 * Va en la pestaña Guion, cuando el libro todavía no tiene guion. No toca Word: el motor lee la biblia (y la
 * escaleta y el estilo, si están en la carpeta), propone el guion en segundo plano y deja un Word NUEVO en la
 * carpeta. Ese Word no se trae solo: el autor lo revisa, lo cambia y pulsa «Traer ese guion».
 *
 * Gasta de la llave del autor, así que va en dos pasos: primero se dice cuánto costará, luego se pide.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./guionApi";
import { dolares, type EstadoDeLaPropuesta, type TrabajoDelMotor } from "./guionLogica";
import { reportPanelError } from "./companion";

const TENUE = "var(--hi-tenue)";
const GD = "var(--hi-oficio)";
const CY = "var(--hi-humano)";
const COLOR = { bien: "var(--hi-bien-suave)", aviso: "var(--hi-aviso)", error: "var(--hi-mal-suave)" } as const;
const CADA = 4000;   // ms entre preguntas mientras trabaja

export type PuenteDeLaPropuesta = Pick<typeof api, "estadoDeLaPropuesta" | "pedirPropuesta" | "abrirPropuesta">;
const DE_VERDAD: PuenteDeLaPropuesta = { estadoDeLaPropuesta: api.estadoDeLaPropuesta, pedirPropuesta: api.pedirPropuesta, abrirPropuesta: api.abrirPropuesta };

const NOMBRE_DE = { biblia: "la biblia", escaleta: "la escaleta", estilo: "la guía de estilo" } as const;
const enLista = (x: string[]) => (x.length < 2 ? x.join("") : `${x.slice(0, -1).join(", ")} y ${x[x.length - 1]}`);
const miles = (n: number) => n.toLocaleString("es-ES");
const unos = (usd: number | null | undefined) => (typeof usd === "number" ? `unos ${dolares(usd)}` : "no sé cuánto: no tengo los precios del modelo ahora mismo");
const tarda = (min: number | undefined) => (typeof min === "number" ? `unos ${min} minutos` : "unos minutos");

export default function GuionPropuesto({ dir, alTerminar, puente = DE_VERDAD }: { dir: string; alTerminar?: () => void; puente?: PuenteDeLaPropuesta }) {
  const [estado, setEstado] = useState<EstadoDeLaPropuesta | null>(null);
  const [alcance, setAlcance] = useState<"uno" | "todos">("uno");
  const [paso, setPaso] = useState<"elegir" | "confirmar" | "pidiendo">("elegir");
  const [error, setError] = useState<string | null>(null);
  const reloj = useRef<ReturnType<typeof setTimeout> | null>(null);

  const leer = useCallback(async () => {
    try { setEstado(await puente.estadoDeLaPropuesta(dir)); } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setError(String(err?.message ?? e));
      if (!err?.codigo) reportPanelError("guion:propuesta", e);
    }
  }, [dir, puente]);
  useEffect(() => { void leer(); }, [leer]);

  // Mientras trabaja se pregunta cada pocos segundos, sin leer los documentos otra vez. Al acabar, lo de siempre.
  const trabajo: TrabajoDelMotor | null = estado?.trabajo ?? null;
  const enMarcha = trabajo?.estado === "en_curso";
  useEffect(() => {
    if (!enMarcha) return;
    reloj.current = setTimeout(async () => {
      try {
        const r = await puente.estadoDeLaPropuesta(dir, true);
        if (r.trabajo?.estado === "en_curso") setEstado((e) => ({ ...(e ?? {}), trabajo: r.trabajo }));
        else { await leer(); alTerminar?.(); }
      } catch { setEstado((e) => (e ? { ...e } : e)); }   // sin motor un momento: se vuelve a preguntar
    }, CADA);
    return () => { if (reloj.current) clearTimeout(reloj.current); };
  }, [enMarcha, estado, dir, puente, leer, alTerminar]);

  const pedir = async () => {
    setPaso("pidiendo"); setError(null);
    try {
      await puente.pedirPropuesta(dir, alcance);
      setEstado(await puente.estadoDeLaPropuesta(dir, true).then((r) => ({ ...(estado ?? {}), trabajo: r.trabajo })));
    } catch (e) {
      const err = e as { message?: string; codigo?: string };
      setError(String(err?.message ?? e));
      if (!err?.codigo) reportPanelError("guion:propuesta", e);
    } finally { setPaso("elegir"); }
  };
  const abrir = async (nombre: string) => {
    try { const r = await puente.abrirPropuesta(dir, nombre); if (!r.abierto) setError(`Ábrelo desde la carpeta del libro: «${nombre}».`); }
    catch (e) { setError(String((e as Error)?.message ?? e)); }
  };

  if (!estado) return null;
  if (!estado.hay && !trabajo) return null;   // sin biblia, la pestaña ya lo dice
  const fuentes = estado.fuentes ?? [];
  const est = estado.estimacion;
  const coste = alcance === "uno" ? est?.uno : est?.todos;
  const hecho = trabajo?.estado === "hecho" ? trabajo.resultado ?? null : null;

  return (
    <div style={{ borderTop: "1px solid var(--hi-raya)", marginTop: 10, paddingTop: 10 }}>
      <div style={rotulo}>Que tu coach te lo proponga</div>
      {fuentes.length > 0 && (
        <p style={{ ...nota, marginTop: 0 }}>
          Tu coach lee {enLista(fuentes.map((f) => NOMBRE_DE[f.tipo] ?? f.nombre))} de esta carpeta y te propone el guion: los capítulos, sus escenas y la ficha de cada una. Te lo deja en un Word nuevo, sin tocar tus documentos. Es una propuesta: la revisas, la cambias y la traes.
          {fuentes.some((f) => f.recortado) && " Algún documento es muy largo y solo se leerá su principio."}
        </p>
      )}
      {fuentes.length > 0 && (
        <p style={{ ...nota, fontSize: 11 }}>{fuentes.map((f) => `${f.nombre} (${miles(f.palabras)} palabras)`).join(" · ")}</p>
      )}

      {enMarcha && trabajo && (
        <div style={{ fontSize: 12, marginTop: 6 }}>
          <div style={{ color: COLOR.aviso, fontWeight: 700 }}>Tu coach está proponiendo el guion…</div>
          {(trabajo.pasos ?? []).map((p) => (
            <div key={p.titulo} style={{ color: p.estado === "hecho" ? COLOR.bien : p.estado === "en_curso" ? "var(--hi-tinta)" : TENUE }}>
              {p.estado === "hecho" ? "✓" : p.estado === "en_curso" ? "…" : "·"} {p.titulo}
            </div>
          ))}
          {trabajo.progreso?.fase && <div style={{ color: TENUE, marginTop: 2 }}>{trabajo.progreso.fase}</div>}
          <p style={nota}>Tarda unos minutos. Puedes seguir trabajando; te lo digo aquí cuando esté.</p>
        </div>
      )}

      {hecho && (
        <div style={{ fontSize: 12, color: COLOR.bien, marginTop: 6 }}>
          <b>Listo: «{hecho.nombre}».</b> {hecho.capitulos} {hecho.capitulos === 1 ? "capítulo" : "capítulos"} y {hecho.escenas} escenas
          {hecho.desarrollados.length === hecho.capitulos ? ", todas con su ficha." : hecho.desarrollados.length
            ? `; llevan ficha las del capítulo${hecho.desarrollados.length > 1 ? "s" : ""} ${enLista(hecho.desarrollados.map(String))}. Las demás tienen su resumen y la ficha en blanco.` : "; las fichas están en blanco."}
          {hecho.coste > 0 && ` Ha costado ${dolares(hecho.coste)}.`}
          {hecho.corte && <div style={{ color: COLOR.aviso, marginTop: 4 }}>Se cortó en el capítulo {hecho.corte.capitulo}: {hecho.corte.motivo} Lo de antes sí está en el Word.</div>}
          {hecho.notas.length > 0 && (
            <div style={{ color: "var(--hi-tinta)", marginTop: 6 }}>
              Lo que tu coach ha decidido porque no estaba en tus documentos:
              <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>{hecho.notas.map((n) => <li key={n}>{n}</li>)}</ul>
            </div>
          )}
          <button onClick={() => abrir(hecho.nombre)} style={{ ...boton, marginTop: 8 }}>Abrirlo en Word</button>
          <p style={nota}>Revísalo y cámbialo en Word. Cuando lo des por bueno, guárdalo y pulsa aquí arriba «Traer ese guion».</p>
        </div>
      )}
      {trabajo && (trabajo.estado === "fallido" || trabajo.estado === "interrumpido") && (
        <div style={{ fontSize: 12, color: COLOR.error, marginTop: 6 }}>
          {trabajo.estado === "interrumpido" ? "HumanInk se cerró mientras proponía el guion: no ha quedado nada." : trabajo.error ?? "No ha salido."} Puedes volver a pedirlo.
        </div>
      )}

      {!enMarcha && estado.hay && (
        <>
          <label style={opcion}>
            <input type="radio" checked={alcance === "uno"} onChange={() => { setAlcance("uno"); setPaso("elegir"); }} />
            <span><b>Probar con el capítulo 1.</b> Todos los capítulos y sus escenas, y la ficha completa solo de las del primero · {unos(est?.uno)}, {tarda(est?.minutos?.uno)}</span>
          </label>
          <label style={opcion}>
            <input type="radio" checked={alcance === "todos"} onChange={() => { setAlcance("todos"); setPaso("elegir"); }} />
            <span><b>Todo el guion.</b> Con la ficha de cada escena{est?.capitulos ? ` (si son unos ${est.capitulos} capítulos)` : ""} · {unos(est?.todos)}, {tarda(est?.minutos?.todos)}</span>
          </label>
          {paso === "elegir" && (
            <button onClick={() => setPaso("confirmar")} style={{ ...boton, marginTop: 8 }}>{hecho ? "Proponer otro" : "Proponer el guion"}</button>
          )}
          {paso !== "elegir" && (
            <div style={{ border: "1px solid var(--hi-aviso-raya)", borderRadius: 8, padding: 10, marginTop: 8, fontSize: 12 }}>
              Va a gastar {unos(coste)} de tu llave de OpenRouter y tardará {tarda(alcance === "todos" ? est?.minutos?.todos : est?.minutos?.uno)}. Son cifras aproximadas. El Word que salga será nuevo: no toca nada de lo que tienes.
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={pedir} disabled={paso === "pidiendo"} style={boton}>{paso === "pidiendo" ? "Pidiendo…" : "Sí, proponerlo"}</button>
                <button onClick={() => setPaso("elegir")} disabled={paso === "pidiendo"} style={secundario}>Ahora no</button>
              </div>
            </div>
          )}
        </>
      )}
      {error && <div style={{ fontSize: 12, color: COLOR.error, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

const nota: React.CSSProperties = { fontSize: 12, color: TENUE, margin: "6px 0" };
const rotulo: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: GD, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 };
const opcion: React.CSSProperties = { display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, marginTop: 6, cursor: "pointer" };
const boton: React.CSSProperties = { padding: "8px 12px", borderRadius: 8, border: "none", background: CY, color: "var(--hi-sobre-acento)", fontWeight: 700, fontSize: 12, cursor: "pointer" };
const secundario: React.CSSProperties = { ...boton, background: "transparent", color: "var(--hi-tinta)", border: "1px solid var(--hi-raya)" };
